type SharePayload = {
  title: string;
  text?: string;
  url: string;
  imageUrl?: string | null;
  mode?: "link_preview" | "with_image";
};

export type ShareOutcome =
  | { status: "shared"; usedImage: boolean }
  | { status: "copied" }
  | { status: "unsupported" }
  | { status: "cancelled" }
  | { status: "error" };

const MEDIUM_IMAGE_MAX_DIMENSION = 1280;
const MEDIUM_IMAGE_QUALITY = 0.72;

const toSafeFileName = (value: string) =>
  String(value || "article")
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64) || "article";

const extensionFromMime = (mime: string) => {
  const normalized = String(mime || "").toLowerCase();
  if (normalized.includes("png")) return "png";
  if (normalized.includes("webp")) return "webp";
  if (normalized.includes("gif")) return "gif";
  if (normalized.includes("jpeg") || normalized.includes("jpg")) return "jpg";
  return "jpg";
};

const toCanvasBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

const loadImageFromBlob = (blob: Blob) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to decode image"));
    };
    image.src = objectUrl;
  });

const convertToMediumQualityImage = async (blob: Blob) => {
  if (typeof document === "undefined") {
    return blob;
  }

  const mime = String(blob.type || "").toLowerCase();
  if (!mime.startsWith("image/") || mime.includes("gif")) {
    return blob;
  }

  try {
    const image = await loadImageFromBlob(blob);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    if (!sourceWidth || !sourceHeight) {
      return blob;
    }

    const maxSide = Math.max(sourceWidth, sourceHeight);
    const scale = maxSide > MEDIUM_IMAGE_MAX_DIMENSION ? MEDIUM_IMAGE_MAX_DIMENSION / maxSide : 1;
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      return blob;
    }

    context.drawImage(image, 0, 0, width, height);
    const optimized = await toCanvasBlob(canvas, "image/jpeg", MEDIUM_IMAGE_QUALITY);
    return optimized || blob;
  } catch {
    return blob;
  }
};

const tryCopyShareUrl = async (url: string) => {
  if (!navigator.clipboard?.writeText) {
    return;
  }

  try {
    await navigator.clipboard.writeText(url);
  } catch {
    // Ignore clipboard failures during share flow.
  }
};

const createImageFileForShare = async (imageUrl: string, title: string) => {
  const response = await fetch(imageUrl, { mode: "cors", credentials: "omit", cache: "no-store" });
  if (!response.ok) {
    return null;
  }

  const sourceBlob = await response.blob();
  if (!sourceBlob.type.startsWith("image/")) {
    return null;
  }

  const optimizedBlob = await convertToMediumQualityImage(sourceBlob);
  const fileName = `${toSafeFileName(title)}.${extensionFromMime(optimizedBlob.type)}`;
  return new File([optimizedBlob], fileName, { type: optimizedBlob.type || "image/jpeg" });
};

export const shareContent = async ({
  title,
  text,
  url,
  imageUrl,
  mode = "link_preview",
}: SharePayload): Promise<ShareOutcome> => {
  const normalizedTitle = String(title || "").trim();
  const normalizedText = String(text || "").trim();
  const normalizedUrl = String(url || "").trim();
  const normalizedImageUrl = String(imageUrl || "").trim();
  const textWithUrl = normalizedText ? `${normalizedText}\n${normalizedUrl}` : normalizedUrl;

  if (typeof navigator.share === "function") {
    // Try copying first while user gesture is still active (more reliable on some browsers).
    void tryCopyShareUrl(normalizedUrl);

    // IMPORTANT:
    // For Telegram/WhatsApp rich preview, sharing URL-only is more reliable than file share.
    // So file share is opt-in via mode="with_image".
    if (mode === "with_image" && normalizedImageUrl) {
      try {
        const imageFile = await createImageFileForShare(normalizedImageUrl, normalizedTitle);
        if (imageFile) {
          const dataWithFile: ShareData = {
            title: normalizedTitle,
            text: textWithUrl,
            url: normalizedUrl,
            files: [imageFile],
          };

          if (!navigator.canShare || navigator.canShare(dataWithFile)) {
            await navigator.share(dataWithFile);
            void tryCopyShareUrl(normalizedUrl);
            return { status: "shared", usedImage: true };
          }
        }
      } catch {
        // Ignore file-share failures and continue with URL share fallback.
      }
    }

    try {
      await navigator.share({
        title: normalizedTitle,
        text: textWithUrl,
        url: normalizedUrl,
      });
      void tryCopyShareUrl(normalizedUrl);
      return { status: "shared", usedImage: false };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return { status: "cancelled" };
      }
      return { status: "error" };
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      const lines = [normalizedTitle];
      if (normalizedText) {
        lines.push(normalizedText);
      }
      lines.push(normalizedUrl);

      await navigator.clipboard.writeText(lines.join("\n"));
      return { status: "copied" };
    } catch {
      return { status: "error" };
    }
  }

  return { status: "unsupported" };
};
